import { useStore } from '@/plugins'
import { confirm, handleAsync, setSnackbar } from 'best-modules/plugins'
import { computed } from 'vue'
import { openDialog } from '@/dialog'
import { useRoute } from 'vue-router'
import { navigateToItem } from '@/utils/helpers'
import { useCan } from '@/composables/useCan'
import {
  taskComplete as taskCompleteRequest,
  taskDelete as taskDeleteRequest,
  taskFinalComplete as taskFinalCompleteRequest,
  taskReject as taskRejectRequest,
  taskRevision as taskRevisionRequest,
  taskTakeToWork as taskTakeToWorkRequest,
} from '@/requests/task'

export function useTaskActions() {
  const store = useStore()
  const route = useRoute()
  const { can } = useCan()
  const userId = computed(() => store.state.user.userData?.id)
  const absenceId = route?.params?.absenceId
  const disabled = task => {
    const isExecutor = computed(() =>
      [task.executorId, task.executor?.id].includes(userId.value)
    )
    const isAuthor = computed(() => {
      return [task.authorId, task.author?.id].includes(userId.value)
    })

    const takeToWork = computed(() => !isExecutor.value || task.statusId !== 1)
    const complete = computed(
      () => !can('tasks.complete') || !isExecutor.value || task.statusId !== 2
    )
    const copy = computed(() => !can('tasks.create') || !isAuthor.value)
    const delegate = computed(() => {
      return (
        !can('tasks.delegate') ||
        !!isAuthor.value ||
        task.isDelegated ||
        [3, 4].includes(task.statusId)
      )
    })
    const reject = computed(() => {
      return (
        !can('tasks.reject') || isAuthor.value || [3, 4].includes(task.statusId)
      )
    })
    const finalComplete = computed(
      () => !can('tasks.complete') || !isAuthor.value || task.statusId !== 5
    )
    const revision = computed(
      () => !can('tasks.revision') || !isAuthor.value || task.statusId !== 5
    )
    const del = computed(() => !can('tasks.delete') || !isAuthor.value)

    return {
      takeToWork,
      complete,
      copy,
      delegate,
      reject,
      finalComplete,
      revision,
      del,
    }
  }
  const taskTakeToWork = (task, loaderName = 'taskTakeToWork') => {
    return handleAsync(loaderName, () => {
      return taskTakeToWorkRequest(task.id, absenceId).then(res => {
        Object.assign(task, res)
        return res
      })
    })
  }
  const taskComplete = (
    task,
    loaderName = 'taskComplete',
    onClose = () => false
  ) => {
    openDialog({
      name: 'Textarea',
      params: {
        validationScope: 'task',
        cb(completionReport) {
          return handleAsync(loaderName, () => {
            return taskCompleteRequest(
              task.id,
              completionReport,
              absenceId
            ).then(res => {
              Object.assign(task, res)
              setSnackbar({
                text: 'Завдання відправлено на перевірку, очікуйте підтвердження виконання автором.',
              })
              onClose(res)
            })
          })
        },
      },
      dialogData: {
        placeholder: 'Звіт про виконання завдання',
        dialogTitle: 'Завершення завдання',
      },
    })
  }

  const taskReject = (
    task,
    loaderName = 'taskReject',
    onClose = () => false
  ) => {
    openDialog({
      name: 'Textarea',
      params: {
        cb(rejectComment) {
          handleAsync(loaderName, () => {
            return taskRejectRequest(task.id, rejectComment, absenceId).then(
              res => {
                Object.assign(task, res)
                setSnackbar({ text: 'Завдання завершено' })
                onClose(res)
              }
            )
          })
        },
      },
      dialogData: {
        dialogTitle: 'Відхилення завдання',
        placeholder: 'Коментар до відхилення',
      },
    })
  }

  const taskFinalComplete = (task, loaderName = 'finalCompleteHandler') => {
    return handleAsync(loaderName, () => {
      return taskFinalCompleteRequest(task.id, absenceId).then(res => {
        Object.assign(task, res)
        setSnackbar({ text: 'Завдання перевірено і завершено' })
        return res
      })
    })
  }

  const taskRevision = (task, loaderName = 'revisionTaskHandler') => {
    return handleAsync(loaderName, () => {
      return taskRevisionRequest(task.id, absenceId).then(res => {
        Object.assign(task, res)
        setSnackbar({
          text: 'Завдання відправлено на доопрацювання виконавцю',
        })
        return res
      })
    })
  }

  const taskDelete = (task, loaderName = 'taskDelete', onClose) => {
    confirm({
      text: 'Ви впевненні що хочете видалити завдання назавжди?',
    }).then(() => {
      return handleAsync(loaderName, () => {
        return taskDeleteRequest(task.id).then(() => {
          setSnackbar({ text: 'Завдання видалено' })
          onClose(task.id)
        })
      })
    })
  }

  const delegateTask = task => {
    openDialog({
      name: 'TaskDelegation',
      dialogData: {
        taskId: task.id,
      },
      params: {
        cb(res) {
          task.executor = res.executor
          task.executorId = res.executorId
          return res
        },
      },
    })
  }

  const copyTask = (task, onClose = () => false) => {
    openDialog({
      name: 'CopyTask',
      dialogData: {
        taskId: task.id,
      },
      params: {
        cb(res) {
          onClose()
          navigateToItem('single-task', res.id)
        },
      },
    })
  }

  const taskActions = task => {
    return [
      {
        title: 'Копіювати завдання',
        disabled: ![task.authorId, task.author?.id].includes(userId.value),
        action: () => copyTask(task),
        icon: 'mdi-content-copy',
        iconProps: {
          color: 'blue-darken-3',
        },
      },
      {
        title: 'Додати підзавдання',
        disabled: true,
        action: () => setSnackbar({ text: 'В розробці', color: 'warning' }),
        icon: 'mdi-plus',
        iconProps: {
          color: 'blue-darken-3',
        },
      },
      {
        title: 'Делегувати',
        disabled: ![task.executorId].includes(userId.value),
        action: () => delegateTask(task),
        icon: 'mdi-account-switch',
        iconProps: {
          color: 'blue-darken-3',
        },
      },
      {
        title: 'Відхилити завдання',
        disabled: disabled(task).reject.value,
        action: () => taskReject(task),
        icon: 'mdi-close-box',
        listItemProps: {
          class: 'text-error',
        },
        iconProps: {
          color: 'error',
        },
      },
      {
        title: 'Видалити ',
        disabled: disabled(task).del.value,
        action: () => taskDelete(task),
        icon: 'mdi-trash-can-outline',
        listItemProps: {
          class: 'text-error',
        },
        iconProps: {
          color: 'error',
        },
      },
    ]
  }
  return {
    disabled,
    taskTakeToWork,
    taskComplete,
    taskFinalComplete,
    taskRevision,
    taskReject,
    taskDelete,
    taskActions,
    delegateTask,
    copyTask,
  }
}
